import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, UserX, ShieldCheck, Clock, Mail, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const POINTS = [
  { icon: Clock, text: "Your account is hidden right away: profile, reels and live are not visible to anyone." },
  { icon: ShieldCheck, text: "For 30 days all your data (reels, wallet, followers) stays safely saved by Hifireels." },
  { icon: UserX, text: "Changed your mind? Just log in within 30 days and tap “Recover my account”." },
  { icon: Mail, text: "After 30 days, Hifireels Support sends a recovery link to your registered email." },
];

export default function DeleteAccount() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!agree || busy) return;
    setBusy(true); setErr("");
    try {
      await api.post("/account/delete", { password, reason });
      // Navigate first: logout() sets user=false which makes <Protected> redirect to plain /login
      nav("/login?deleted=1", { replace: true });
      await logout();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-6" data-testid="delete-account-page">
      <div className="flex items-center gap-3 mb-1">
        <Link to="/help" data-testid="delete-back"><ArrowLeft className="w-5 h-5"/></Link>
        <h1 className="font-outfit font-extrabold text-2xl tracking-tighter">Delete my Account/Data</h1>
      </div>
      <p className="text-white/60 text-xs mb-5 ml-8">@{user?.username} · {user?.email}</p>

      <div className="space-y-2 mb-5">
        {POINTS.map((p, i) => (
          <div key={i} className="flex gap-3 p-3 rounded-2xl bg-white/5 border border-white/10">
            <p.icon className="w-4 h-4 text-[#FFD700] flex-shrink-0 mt-0.5"/>
            <p className="text-xs text-white/80 leading-relaxed">{p.text}</p>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-3 p-4 rounded-3xl bg-[#FF2A5F]/5 border border-[#FF2A5F]/30">
        <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3} maxLength={500} data-testid="delete-reason"
          placeholder="Why are you leaving? (optional)"
          className="w-full p-3 rounded-2xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF2A5F]/60"/>
        <input type="password" required value={password} onChange={e=>setPassword(e.target.value)} data-testid="delete-password"
          placeholder="Enter your password to confirm"
          className="w-full p-3 rounded-2xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF2A5F]/60"/>
        <label className="flex items-start gap-2 text-xs text-white/75">
          <input type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)} data-testid="delete-agree" className="mt-0.5 accent-[#FF2A5F]"/>
          I understand my account will be hidden now and I can recover it within 30 days.
        </label>
        {err && <p className="text-xs text-[#FF2A5F]" data-testid="delete-error">{err}</p>}
        <button disabled={!agree || !password || busy} data-testid="delete-submit"
          className="w-full p-3 rounded-2xl bg-[#FF2A5F] font-extrabold disabled:opacity-40 flex items-center justify-center gap-2 active:scale-[0.99]">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <UserX className="w-4 h-4"/>}Delete my account
        </button>
      </form>
    </div>
  );
}
